import {
  FLOOD_FORBIDDEN_ROLES,
  FLOOD_UNLOCK_UPGRADE_ID,
  HULL_PATCH_IRON_MIN_INTEGRITY,
  HULL_ZONE_IDS,
  HULL_ZONE_LABELS,
  PATCH_PRODUCT_ID_BY_TIER,
  PATCH_TIER_LABELS,
  patchRestoresIntegrity,
  type PatchTier
} from '../../shared/constants.js';
import type { GameSnapshot, HullZoneId, Role } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import type { ControllerNetwork } from '../controllerNetwork.js';
import { createCard } from '../controllerUiHelpers.js';
import { createSubmarineMapView, setZonePatchable, type SubmarineMapView } from '../submarineMap/submarineMapView.js';
import { t } from '../i18n.js';

type EngineeringRoleMeta = { label: string; icon: string };

type CreateEngineeringRoomControllerOptions = {
  controls: HTMLDivElement | null;
  resolveRoleMeta: (role: Role) => EngineeringRoleMeta;
  getControllerNetwork: () => ControllerNetwork | null;
};

const integrityColor = (pct: number): string =>
  pct > 75 ? '#4fc3f7' : pct >= HULL_PATCH_IRON_MIN_INTEGRITY ? '#ffd54f' : '#ef5350';

const FLOOD_REQUIREMENT_TEXT = 'Locked: buy the Intentional Flooding upgrade (unlock-flood) at a base to open a room\'s valves for a fast dive.';

/** Count how many patches of a given product id are in shared cargo. */
const patchCount = (patchId: string): number =>
  controllerState.lastRoomState?.cargo[patchId] ?? 0;

export const createEngineeringRoomController = ({
  controls,
  resolveRoleMeta,
  getControllerNetwork
}: CreateEngineeringRoomControllerOptions) => {
  let pumpPanelBody: HTMLDivElement | null = null;
  let mapView: SubmarineMapView | null = null;
  // The pump-assignment popup overlay (appended to <body>, so snapshot ticks never clip/close it).
  let pumpPopup: HTMLDivElement | null = null;

  const emit = getControllerNetwork;
  const roomCode = (): string | null => controllerState.currentRoomCode;

  // ── Pump status panel ──────────────────────────────────────────────────────
  const renderPumpPanel = (): void => {
    if (!pumpPanelBody) return;
    pumpPanelBody.innerHTML = '';
    const submarine = controllerState.lastSnapshot?.submarine;
    const pumps = submarine?.bilgePumps ?? [
      { assignedRoom: null, active: false },
      { assignedRoom: null, active: false }
    ];

    const floodLine = document.createElement('div');
    floodLine.className = 'pump-row';
    floodLine.dataset.floodStatus = 'true';
    const floodOwned = controllerState.lastRoomState?.ownedUpgrades?.includes(FLOOD_UNLOCK_UPGRADE_ID) ?? false;
    const orders = Object.entries(submarine?.floodOrders ?? {});
    floodLine.textContent = !floodOwned
      ? FLOOD_REQUIREMENT_TEXT
      : orders.length === 0
        ? 'Flood valves: ready. Tap a room, then Flood this room.'
        : `Flood valves: ${orders.map(([r, o]) => `${resolveRoleMeta(r as Role).label} ${o!.state === 'warning' ? `in ${Math.ceil(o!.ticks / 10)} s` : o!.state === 'open' ? `${Math.round(submarine?.roomFlood?.[r as Role] ?? 0)}%` : 'draining'}`).join(', ')}`;
    floodLine.style.fontSize = '0.8em';
    floodLine.style.opacity = floodOwned ? '1' : '0.7';
    pumpPanelBody.appendChild(floodLine);

    ([0, 1] as const).forEach((pumpIndex) => {
      const pump = pumps[pumpIndex];
      const row = document.createElement('div');
      row.className = 'pump-row';

      const name = document.createElement('span');
      name.className = 'pump-row-name';
      name.textContent = `PUMP ${pumpIndex + 1}`;

      const assigned = document.createElement('span');
      assigned.className = 'pump-row-assigned' + (pump.assignedRoom ? '' : ' pump-row-unassigned');
      assigned.textContent = pump.assignedRoom ? resolveRoleMeta(pump.assignedRoom).label : 'UNASSIGNED';

      const drain = document.createElement('div');
      drain.className = 'pump-drain-bar' + (pump.active ? ' pump-drain-active' : '');
      const drainFill = document.createElement('div');
      drainFill.className = 'pump-drain-fill';
      drain.appendChild(drainFill);

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'pump-toggle' + (pump.active ? ' pump-toggle-active' : '');
      toggle.textContent = pump.active ? 'ON' : 'OFF';
      toggle.addEventListener('click', () => {
        const code = roomCode();
        if (!code) return;
        const next = !pump.active;
        if (!next) {
          // Turning a pump off unassigns it and stops the drain.
          emit()?.engineerAction({ code, action: 'bilge_toggle', pumpIndex, active: false });
          emit()?.engineerAction({ code, action: 'bilge_assign', pumpIndex, room: null });
        } else if (pump.assignedRoom) {
          emit()?.engineerAction({ code, action: 'bilge_toggle', pumpIndex, active: true });
        }
      });

      const unassign = document.createElement('button');
      unassign.type = 'button';
      unassign.className = 'pump-unassign';
      unassign.textContent = t('unassign');
      unassign.disabled = !pump.assignedRoom;
      unassign.addEventListener('click', () => {
        const code = roomCode();
        if (!code || !pump.assignedRoom) return;
        emit()?.engineerAction({ code, action: 'bilge_assign', pumpIndex, room: null });
      });

      row.append(name, assigned, drain, toggle, unassign);
      pumpPanelBody!.appendChild(row);
    });
  };

  // ── Hull-zone detail popup ─────────────────────────────────────────────────
  let hullPopup: HTMLDivElement | null = null;

  const closeHullPopup = (): void => {
    hullPopup?.remove();
    hullPopup = null;
  };

  const openHullZonePopup = (zoneId: HullZoneId): void => {
    closeHullPopup();
    closePumpPopup();

    const submarine = controllerState.lastSnapshot?.submarine;
    const code = roomCode();
    if (!submarine || !code) return;

    const integrity = submarine.hullZones[zoneId] ?? 100;
    const breached = submarine.hullBreaches[zoneId] ?? false;
    const zoneName = HULL_ZONE_LABELS[zoneId];

    const overlay = document.createElement('div');
    overlay.className = 'engineer-pump-popup-overlay';
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeHullPopup();
    });

    const popup = document.createElement('div');
    popup.className = 'engineer-pump-popup';

    const title = document.createElement('div');
    title.className = 'engineer-pump-popup-title';
    title.textContent = `Hull zone · ${zoneName}`;
    popup.appendChild(title);

    const integrityRow = document.createElement('div');
    integrityRow.className = 'engineer-hull-popup-stat';
    integrityRow.innerHTML = `<span>Integrity</span><span style="color:${integrityColor(integrity)};font-weight:700">${Math.round(integrity)}%</span>`;
    popup.appendChild(integrityRow);

    const statusRow = document.createElement('div');
    statusRow.className = 'engineer-hull-popup-stat';
    statusRow.innerHTML = `<span>Status</span><span style="color:${breached ? '#ef5350' : '#4ade80'};font-weight:700">${breached ? 'LEAKING' : 'Sealed'}</span>`;
    popup.appendChild(statusRow);

    const PATCH_TIERS: PatchTier[] = ['iron', 'composite', 'titanium'];
    // A sealed but damaged zone can still take the patch tiers that restore integrity.
    const applicableTiers = PATCH_TIERS.filter((tier) => breached || (integrity < 100 && patchRestoresIntegrity(tier)));
    {
      for (const tier of applicableTiers) {
        const productId = PATCH_PRODUCT_ID_BY_TIER[tier];
        const count = patchCount(productId);
        const canUse = count > 0;
        const label = PATCH_TIER_LABELS[tier];
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'engineer-pump-pick' + (canUse ? ' engineer-pump-pick-active' : '');
        btn.style.marginTop = '0.5rem';
        btn.style.width = '100%';
        btn.style.textAlign = 'center';
        btn.disabled = !canUse;
        btn.textContent = canUse ? `${label} (${count})` : `${label} — none`;
        btn.addEventListener('click', () => {
          emit()?.engineerAction({ code, action: 'patch_hull', zone: zoneId, tier });
          closeHullPopup();
        });
        popup.appendChild(btn);
      }
    }

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'engineer-pump-pick engineer-pump-pick-clear';
    closeBtn.style.marginTop = '0.4rem';
    closeBtn.style.width = '100%';
    closeBtn.textContent = t('close');
    closeBtn.addEventListener('click', closeHullPopup);
    popup.appendChild(closeBtn);

    overlay.appendChild(popup);
    document.body.appendChild(overlay);
    hullPopup = overlay;
  };

  // ── Pump-assignment popup (separate overlay) ────────────────────────────────
  const closePumpPopup = (): void => {
    pumpPopup?.remove();
    pumpPopup = null;
  };

  const openPumpPopup = (room: Role): void => {
    closePumpPopup();

    const submarine = controllerState.lastSnapshot?.submarine;
    const pumps = submarine?.bilgePumps;
    const assignedPump = pumps ? pumps.findIndex((p) => p.assignedRoom === room) : -1;

    const overlay = document.createElement('div');
    overlay.className = 'engineer-pump-popup-overlay';
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closePumpPopup();
    });

    const popup = document.createElement('div');
    popup.className = 'engineer-pump-popup';

    const title = document.createElement('div');
    title.className = 'engineer-pump-popup-title';
    title.textContent = `Assign pump · ${resolveRoleMeta(room).label}`;
    popup.appendChild(title);

    const choices = document.createElement('div');
    choices.className = 'engineer-pump-popup-choices';

    ([0, 1] as const).forEach((pumpIndex) => {
      const pump = pumps?.[pumpIndex];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'engineer-pump-pick' + (assignedPump === pumpIndex ? ' engineer-pump-pick-active' : '');
      const occupiedBy = pump?.assignedRoom && pump.assignedRoom !== room
        ? ` (on ${resolveRoleMeta(pump.assignedRoom).label})`
        : '';
      btn.textContent = `Pump ${pumpIndex + 1}${occupiedBy}`;
      btn.addEventListener('click', () => {
        const code = roomCode();
        if (!code) return;
        emit()?.engineerAction({ code, action: 'bilge_assign', pumpIndex, room });
        closePumpPopup();
      });
      choices.appendChild(btn);
    });

    const unassign = document.createElement('button');
    unassign.type = 'button';
    unassign.className = 'engineer-pump-pick engineer-pump-pick-clear';
    unassign.textContent = 'Unassign';
    unassign.disabled = assignedPump < 0;
    unassign.addEventListener('click', () => {
      const code = roomCode();
      if (code && (assignedPump === 0 || assignedPump === 1)) {
        emit()?.engineerAction({ code, action: 'bilge_assign', pumpIndex: assignedPump, room: null });
      }
      closePumpPopup();
    });
    choices.appendChild(unassign);

    popup.appendChild(choices);

    // ── Intentional flooding: valves for a very fast descent (needs the unlock-flood upgrade). ──
    if (!FLOOD_FORBIDDEN_ROLES.has(room) && !room.startsWith('facility_')) {
      const floodTitle = document.createElement('div');
      floodTitle.className = 'engineer-pump-popup-title';
      floodTitle.style.marginTop = '0.7rem';
      floodTitle.textContent = `Flood valve · ${resolveRoleMeta(room).label}`;
      popup.appendChild(floodTitle);

      const owned = controllerState.lastRoomState?.ownedUpgrades?.includes(FLOOD_UNLOCK_UPGRADE_ID) ?? false;
      const order = submarine?.floodOrders?.[room];
      const level = Math.round(submarine?.roomFlood?.[room] ?? 0);
      const floodBtn = document.createElement('button');
      floodBtn.type = 'button';
      floodBtn.dataset.floodControl = room;
      floodBtn.className = 'engineer-pump-pick' + (order && order.state !== 'draining' ? ' engineer-pump-pick-active' : '');
      floodBtn.style.width = '100%';
      floodBtn.style.textAlign = 'center';
      const note = document.createElement('div');
      note.className = 'engineer-hull-popup-stat';
      note.style.fontSize = '0.8em';
      if (!owned) {
        floodBtn.disabled = true;
        floodBtn.textContent = 'Flood room (locked)';
        note.textContent = FLOOD_REQUIREMENT_TEXT;
      } else if (order && order.state !== 'draining') {
        floodBtn.textContent = order.state === 'warning'
          ? `Cancel flood (evacuating ${Math.ceil(order.ticks / 10)} s)`
          : 'Close valves and pump out';
        note.textContent = order.state === 'warning'
          ? 'The crew in this room is being warned. Valves open when the countdown ends.'
          : `Valves open, room ${level}% flooded. Pump-out starts as soon as you close them.`;
      } else if (order) {
        floodBtn.disabled = true;
        floodBtn.textContent = `Pumping out (${level}%)`;
        note.textContent = 'Valves are shut; the bilge pumps are emptying the room.';
      } else {
        floodBtn.textContent = 'Flood this room';
        note.textContent = 'Warns and evacuates the crew, then the room fills and the boat sinks fast. Flooded rooms are unpowered.';
      }
      floodBtn.addEventListener('click', () => {
        const code = roomCode();
        if (!code) return;
        emit()?.engineerAction({ code, action: 'flood_room', room, flood: !(order && order.state !== 'draining') });
        closePumpPopup();
      });
      popup.append(floodBtn, note);
    }

    overlay.appendChild(popup);
    document.body.appendChild(overlay);
    pumpPopup = overlay;
  };

  // ── 2D submarine cutaway (shared component, engineering density) ──────────
  const buildHullDiagram = (): SVGSVGElement => {
    mapView?.dispose();
    mapView = createSubmarineMapView({
      mode: 'engineering',
      resolveRoleMeta,
      onRoomTap: openPumpPopup,
      onZoneTap: openHullZonePopup
    });
    return mapView.el;
  };

  /** Mutate the diagram in place from the latest snapshot. */
  const updateDiagram = (): void => {
    if (!mapView) return;
    const snapshot = controllerState.lastSnapshot ?? null;
    const roomState = controllerState.lastRoomState ?? null;
    mapView.update(roomState, snapshot);

    // Breached zones flash red (the map view does that itself); this also marks the ones that can
    // be patched with what is in storage right now.
    const submarine = snapshot?.submarine;
    if (submarine) {
      const havePatches = Object.values(PATCH_PRODUCT_ID_BY_TIER).some((id) => patchCount(id) > 0);
      for (const zoneId of HULL_ZONE_IDS) {
        const breached = submarine.hullBreaches[zoneId] ?? false;
        const damaged = (submarine.hullZones[zoneId] ?? 100) < 100;
        const haveRepairPatch = (['composite', 'titanium'] as const).some(
          (tier) => patchRestoresIntegrity(tier) && patchCount(PATCH_PRODUCT_ID_BY_TIER[tier]) > 0
        );
        setZonePatchable(mapView, zoneId, (breached && havePatches) || (damaged && haveRepairPatch));
      }
    }
  };

  // ── Render / update / dispose ──────────────────────────────────────────────
  const render = (): void => {
    if (!controls) return;
    dispose();

    const card = createCard('Hull Integrity', 'Tap a hull section to patch a leak; tap a room to assign a bilge pump.');
    card.classList.add('ctrl-room-main-card');

    // Top half — 2D submarine diagram
    const hullSection = document.createElement('div');
    hullSection.className = 'engineer-hull-section';
    hullSection.appendChild(buildHullDiagram());
    card.appendChild(hullSection);

    // Bottom half — pump status
    const pumpSection = document.createElement('div');
    pumpSection.className = 'engineer-pump-section';
    const pumpHeader = document.createElement('div');
    pumpHeader.className = 'section-title engineer-section-title';
    pumpHeader.textContent = t('pump_status');
    pumpSection.appendChild(pumpHeader);
    pumpPanelBody = document.createElement('div');
    pumpPanelBody.className = 'engineer-pump-body';
    pumpSection.appendChild(pumpPanelBody);
    card.appendChild(pumpSection);

    controls.appendChild(card);

    updateDiagram();
    renderPumpPanel();
  };

  const update = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) return;
    updateDiagram();
    renderPumpPanel();
  };

  const dispose = (): void => {
    closeHullPopup();
    closePumpPopup();
    mapView?.dispose();
    mapView = null;
    pumpPanelBody = null;
    if (controls) controls.innerHTML = '';
  };

  return { render, update, dispose };
};
